import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Input,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Checkbox
} from "@mui/material";
import apis from "../../../api";
import { useSelector, useDispatch } from "react-redux";
import {
  setTraitsSource,
  setTraitsDetail,
  setTraitsType,
  setTraitsName,
  setTraitsPopulation,
  setTraitsCol,
} from "../../../store/reducers/job";
import StatusBox from "../../../component/StatusBox";
import AlertWindow from "../../../component/AlertWindow";

export const DataBlock = ({ setStep }) => {
  const dispatch = useDispatch();
  const userEmail = useSelector((state) => state.user.email);
  const store_types = useSelector((state) => state.job.traitsType);
  const store_sources = useSelector((state) => state.job.traitsSource);
  const store_details = useSelector((state) => state.job.traitsDetail);
  const store_names = useSelector((state) => state.job.traitsName);
  const store_population = useSelector((state) => state.job.traitsPopulation);
  const store_col = useSelector((state) => state.job.traitsCol);

  const [name, setName] = useState(store_names[0]);
  const [type, setType] = useState(store_types[0]);
  const [source, setSource] = useState(store_sources[0]);
  const [detail, setDetail] = useState(store_details[0]);
  const [population, setPopulation] = useState(store_population[0]);
  const [colNames, setColNames] = useState(store_col[0]);

  const [nType, setNType] = useState(store_col[0].ncase === "" ? "" : "ncase");
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadConsentChecked, setUploadConsentChecked] = useState(false);
  const [gwas_catalog, setGwas_catalog] = useState([]);
  const [sourceStatus, setSourceStatus] = useState("required");
  const [columnsMatched, setColumnsMatched] = useState(null);

  const [colNamesStatus, setColNamesStatus] = useState("required");
  const [sampleSizeStatus, setSampleSizeStatus] = useState("required");

  const [past_data_names, setPastDataNames] = useState(["No data uploaded"]);
  const [past_data, setPastData] = useState([]);
  const [selectedPastDataName, setSelectedPastDataName] = useState("No data uploaded");

  const [finngen_codes, setFinngenCodes] = useState([]);
  const [finngen_code2n, setFinngenCode2n] = useState({});

  const [gaws_codes, setGwasCodes] = useState([]);

  useEffect(() => {
    fetch("finngen_code2n.json")
      .then((response) => response.json())
      .then((data) => {
        setFinngenCode2n(data);
        setFinngenCodes(Object.keys(data));
      })
      .catch((error) => console.error("Error loading the file:", error));

    fetch("gwas_code.json")
      .then((response) => response.json())
      .then((data) => {
        setGwasCodes(data);
      })
      .catch((error) => console.error("Error loading the file:", error));
  }, []);



  const gwas_catalog_study_prefix = "https://www.ebi.ac.uk/gwas/studies/";


  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessgae, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  const handleAlertClose = () => {
    setAlertOpen(false);
    setAlertMessage("");
    setAlertTitle("");
  };

  useEffect(() => {
    fetch("harmonised.txt")
      .then((response) => response.text())
      .then((data) => {
        let lines = data.split("\n");
        let gwas_catalogData = lines.map((line) => {
          let name = line.split("/")[2];
          let link = gwas_catalog_study_prefix + name;
          return { name, link };
        });
        setGwas_catalog(gwas_catalogData);
      })
      .catch((error) => console.error("Error loading the file:", error));
  }, []);

  const handleLoadPastData = (idx) => {
    setSelectedPastDataName(past_data[idx].traitsName);
    setDetail(past_data[idx].traitsDetail[0]);
    setType(past_data[idx].traitsType[0]);
    setName(past_data[idx].traitsName);
    setPopulation(past_data[idx].traitsPopulation[0]);
    // set all keys in the past_data[idx].traitsCol[0] to lower case\
    let keys = Object.keys(past_data[idx].traitsCol[0]);
    let values = Object.values(past_data[idx].traitsCol[0]);
    let newColNames = {};
    for (let i = 0; i < keys.length; i++) {
      newColNames[keys[i].toLowerCase()] = values[i];
    }
    setColNames(newColNames);
    if (values.some(value => value !== "")) {
      setColumnsMatched("no");
    }
    else {
      setColumnsMatched("yes");
    }
  };

  useEffect(() => {
    apis.getPastData({ email: userEmail }).then((response) => {
      if (response.length > 0) {
        let names = response.map((data) => data.traitsName);
        setPastDataNames(names);
        setPastData(response);
        setSelectedPastDataName("Select Previous Data");
      }
    }
    ).catch((error) => {
      console.log(error);
    });
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const response = await apis.uploadData(file, userEmail);
        setUploadedFileName(file.name);
        setSourceStatus("ok");
      } catch (error) {
        setAlertOpen(true);
        setAlertTitle("❌ Error");
        setAlertMessage("Error while uploading the file. Please try again. Error: " + error);
      }
      setUploading(false);
      setDetail(file.name);
    }
  };

  const handleColNameChange = (e) => {
    const { name, value } = e.target;
    setColNames({
      ...colNames,
      [name]: value,
    });
  };

  const handleRadioChange = (e) => {
    setSource(e.target.value);
    if (e.target.value === "Upload Data" && store_details[0] !== "GWAS Catalog") {
      setUploadedFileName(store_details[0]);
    }
    if (e.target.value !== "Upload Data") {
      setUploadConsentChecked(false);
    }
  };

  useEffect(() => {
    if (source === "Query Data" && detail === "FinnGen") {
      setPopulation("European (EUR)");
    }
  }, [source, detail]);


  useEffect(() => {
    if (source === "Upload Data") {
      setSourceStatus(uploadedFileName === "" ? "required" : "ok");
    }
    if (source === "Query Data") {
      if (detail === "GWAS Catalog") {
        if (colNames.id === "") {
          setSourceStatus("required");
        } else if (!colNames.id.startsWith("GCST")) {
          setSourceStatus("required");
        } else {
          setSourceStatus("ok");
        }
      }
      if (detail === "FinnGen") {
        if (colNames.id === "") {
          setSourceStatus("required");
        }
        else {
          setSourceStatus("ok");
        }
      }
    }
    if (source === "Previous Data") {
      setSourceStatus("ok");
    }
  }, [source, uploadedFileName, colNames, detail]);

  useEffect(() => {
    if (type === "Binary") {
      if (source === "Query Data" || source === "Upload Data") {
        if (colNames.neff !== "") {
          setColNames({ ...colNames, ncase: "", ncontrol: "" });
          setNType("neff");
        }
        else {
          setNType("ncase");
        }
      }
    }
    if (type === "Continuous") {
      setNType("n");
    }
  }, [type]);

  useEffect(() => {
    if (columnsMatched === "yes") {
      setColNamesStatus("ok");
    } else if (columnsMatched === "no") {
      const isAnyColNameFilled = Object.values(colNames).some(value => value !== "");
      setColNamesStatus(isAnyColNameFilled ? "ok" : "required");
    }
  }, [columnsMatched, colNames]);

  useEffect(() => {
    if (source === "Query Data") {
      if (detail === "GWAS Catalog" || detail === "FinnGen") {
        if (type === "Binary") {
          if (colNames.ncase === "" && colNames.ncontrol === "" && colNames.neff === "") {
            setSampleSizeStatus("required");
          } else {
            if (colNames.ncase !== "" && isNaN(colNames.ncase)) {
              // setAlertOpen(true);
              // setAlertTitle("❗️ Error");
              // setAlertMessage("Please enter a number for Ncase.");
              setSampleSizeStatus("required");
              return;
            } else if (colNames.ncontrol !== "" && isNaN(colNames.ncontrol)) {
              // setAlertOpen(true);
              // setAlertTitle("❗️ Error");
              // setAlertMessage("Please enter a number for Ncontrol.");
              setSampleSizeStatus("required");
              return;
            } else if (colNames.neff !== "" && isNaN(colNames.neff)) {
              // setAlertOpen(true);
              // setAlertTitle("❗️ Error");
              // setAlertMessage("Please enter a number for Neff.");
              setSampleSizeStatus("required");
              return;
            } else {
              setSampleSizeStatus("ok");
            }
          }
        } else if (type === "Continuous") {
          if (colNames.n === "") {
            setSampleSizeStatus("required");
          } else {
            if (isNaN(colNames.n)) {
              // setAlertOpen(true);
              // setAlertTitle("❗️ Error");
              // setAlertMessage("Please enter a number for N.");
              setSampleSizeStatus("required");
              return;
            } else {
              setSampleSizeStatus("ok");
            }
          }
        }
      }
    }
  }, [source, detail, type, colNames]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (source === "") {
      // alert("Please select the source of your GWAS summary statistics.");
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please select the source of your GWAS summary statistics.");
      return;
    }
    if (name === "") {
      // alert("Please enter the name of the trait");
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please enter the name of the trait.");
      return;
    }
    if (uploading) {
      // alert("Please wait for the file GWAS upload to be completed.");
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please wait for the file GWAS upload to be completed.");
      return;
    }
    if (source === "Upload Data" && !uploadConsentChecked) {
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please confirm the User Consent and Responsibility Statement before uploading data.");
      return;
    }
    if (source === "Upload Data" && uploadedFileName === "") {
      // alert("Please upload the GWAS file or select other GWAS source.");
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please upload the GWAS file or select other GWAS source.");
      return;
    }
    if (source === "Query Data") {
      if (colNames.id === "") {
        // alert("Please enter the Trait ID for GWAS Catalog.");
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the Study Accession/Phenocode for Queried Data.");
        return;
      }
      if (type === "Binary" && colNames.ncase === "" && colNames.ncontrol === "" && colNames.neff === "") {
        // alert("Please enter the Sample Size for GWAS Catalog.");
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the Sample Size for Queried Data.");
        return;
      }
      if (type === "Continuous" && colNames.n === "") {
        // alert("Please enter the Sample Size for GWAS Catalog.");
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the Sample Size for Queried Data.");
        return;
      }
      if (population === "") {
        // alert("Please select the population of your input data.");
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please select the population of your input data.");
        return;
      }
    }
    if (source === "Query Data" && detail === "GWAS Catalog") {
      if (!colNames.id.startsWith("GCST")) {
        // alert("Please enter the correct Trait ID for GWAS Catalog. The ID should start with 'GCST'. Please see the example in Tutorial.")
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the correct Study Accession for GWAS Catalog. The ID should start with 'GCST'. Please see the example in Tutorial.");
        return;
      }
      else if (colNames.id === "" || !gaws_codes.includes(colNames.id)) {
        // alert("Please enter the correct Trait ID for GWAS Catalog. The ID is not in the provided list of GWAS Catalog IDs.")
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the correct Study Accession for GWAS Catalog. The ID is not in the provided list of GWAS Catalog IDs. Note: Please ensure there is no space before or after the ID.");
        return;
      }
    }
    if (columnsMatched === "no" && colNamesStatus === "required") {
      // alert("Please fill in at least one column name.");
      setAlertOpen(true);
      setAlertTitle("⚠️ Warning");
      setAlertMessage("Please fill in at least one column name.");
      return;
    }
    if (source === "Query Data" && detail === "FinnGen") {
      if (colNames.id === "" || !finngen_codes.includes(colNames.id)) {
        setAlertOpen(true);
        setAlertTitle("⚠️ Warning");
        setAlertMessage("Please enter the correct phenocode for FinnGen. The current input is not in the provided list of FinnGen phenocodes. Note that the phenocode is case-sensitive.");
        return;
      }
    }

    dispatch(setTraitsSource([source]));
    dispatch(setTraitsDetail([detail]));
    dispatch(setTraitsType([type]));
    dispatch(setTraitsName([name]));
    dispatch(setTraitsPopulation([population]));
    dispatch(setTraitsCol([colNames]));

    setStep(2);
  };

  const gridItemStyle = {
    paddingBottom: 2,
    paddingLeft: 2,
    paddingRight: 2,
    border: "1px solid #ddd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  };


  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        mt: 2,
        boxSizing: "border-box",
      }}
    >
      <AlertWindow
        open={alertOpen}
        title={alertTitle}
        message={alertMessgae}
        onClose={handleAlertClose}
      />

      <Box
        sx={{
          width: "95%",
          display: "flex",
          border: "1px solid #ddd",
          borderRadius: "5px",
          overflow: "auto",
          boxSizing: "border-box",
          pt: 2,
          pl: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
        >
          <Grid
            container
            spacing={{ xs: 2, md: 2 }}
            columns={{ xs: 12, sm: 6, md: 12 }}
          >

            {/* Data Sources */}
            <Grid item xs={4} sx={gridItemStyle}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                GWAS Summary Statistics:
              </Typography>
              {source === "Query Data" && (
                <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2">
                    Please enter the information of the GWAS summary statistics.
                  </Typography>
                  <br></br>
                  {detail === "GWAS Catalog" && (
                    <Typography variant="body2">
                      Note: You can query harmonized GWAS Catalog datasets provided on the <a href="/data" target="_blank">Queryable Data</a> page. Simply search for the trait of interest, then start by entering the Study Accession (e.g., GCST90320257). Please check <a href="https://pennprs.gitbook.io/pennprs/user-manual/single-ancestry-analysis#option-2.-query-data-from-the-public-gwas-summary-database-provided-by-pennprs" target="_blank">Tutorial</a> for more information.
                    </Typography>
                  )}
                  {detail === "FinnGen" && (
                    <Typography variant="body2">
                      Note: You can query FinnGen datasets provided on the <a href="/data" target="_blank">Queryable Data</a> page. Simply search for the trait of interest, then enter the Phenocode. Please check <a href="https://pennprs.gitbook.io/pennprs/user-manual/single-ancestry-analysis#option-2.-query-data-from-the-public-gwas-summary-database-provided-by-pennprs
                      " target="_blank">Tutorial</a> for more information.
                    </Typography>)
                  }
                </Box>
              )}
              {source === "Upload Data" && (
                <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2">
                    We support the following file formats: <b>.txt, .tsv, .csv, .zip, .gz, .gzip,</b> and <b>.tar.gz</b>.Maximum file size allowed: <b>800MB</b>.
                    Click to download the example input file for a{" "}
                          <a
                            href="/example_EUR_continuous.txt"
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            continuous trait
                          </a>{" "}
                          and a{" "}
                          <a
                            href="/example_EUR_binary.txt"
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            binary trait
                          </a>
                          .
                  </Typography>
                </Box>
              )}
              {source === "Previous Data" && (
                <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2">
                    All the GWAS summary statistics you have uploaded before are listed here.
                  </Typography>
                  <Typography variant="body2">
                    <br></br> Note: Only the data with successfully job completion will be shown. Please select the one you want to analyze. You may change the trait information if needed, but the changed information will not be saved for future use.
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={4} sx={gridItemStyle}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={source}
                  onChange={(e) => handleRadioChange(e)}
                >
                  <FormControlLabel
                    value="Query Data"
                    control={<Radio />}
                    label="Query Data"
                  />
                  <FormControlLabel
                    value="Upload Data"
                    control={<Radio />}
                    label="Upload Data"
                  />
                  <FormControlLabel
                    value="Previous Data"
                    control={<Radio />}
                    label="Previous Uploaded Data"
                  />
                  {source === "Upload Data" && (
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={uploadConsentChecked}
                            onChange={(e) => setUploadConsentChecked(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            I confirm that the data I am uploading consist only of GWAS summary statistics, do not include individual-level or identifiable information, and that I have the appropriate authorization to use and upload these data. I acknowledge and accept the data security and privacy terms described above and required by the Amazon Web Services <a href="https://pennprs.gitbook.io/pennprs/data-security" target="_blank">[Terms of Use]</a>.
                          </Typography>
                        }
                      />
                      {uploadConsentChecked && uploadedFileName === "" && (
                        <Input type="file" onChange={handleFileChange} sx={{ mt: 0 }} disabled={uploading} />
                      )}
                    </Box>
                  )}
                  {source === "Upload Data" && uploading && (
                    <CircularProgress size={20} />
                  )}
                  {source === "Query Data" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 2,
                        width: "100%",
                      }}
                    >
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          value={detail}
                          onChange={(e) => setDetail(e.target.value)}
                        >
                          <MenuItem value={"GWAS Catalog"}>GWAS Catalog</MenuItem>
                          <MenuItem value={"FinnGen"}>FinnGen</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label={detail === "GWAS Catalog" ? "Study Accession" : "Phenocode"}
                        variant="outlined"
                        value={colNames.id}
                        sx={{ width: "100%" }}
                        onChange={(e) => setColNames({ ...colNames, id: e.target.value })}
                        error={!colNames.id.startsWith("GCST") && colNames.id !== "" && detail === "GWAS Catalog"}
                        helperText={!colNames.id.startsWith("GCST") && colNames.id !== "" && detail === "GWAS Catalog" ? "The ID should start with 'GCST'." : ""}
                      />
                    </Box>
                  )}
                  {source === "Previous Data" && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                        width: '100%',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                      }}
                    >
                      <FormControl variant="outlined">
                        <Select
                          value={selectedPastDataName}
                          onChange={(e) => {
                            setSelectedPastDataName(e.target.value);
                            handleLoadPastData(past_data_names.indexOf(e.target.value));
                          }}
                          SelectDisplayProps={{
                            style: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '90%',
                              whiteSpace: 'nowrap',
                            },
                          }}
                          sx={{
                            maxWidth: '90%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {past_data_names.map((name, idx) => (
                            <MenuItem key={idx} value={name} sx={{
                              maxWidth: '100% !important',
                              overflow: 'hidden !important',
                              whiteSpace: 'nowrap !important',
                              textOverflow: 'ellipsis !important',
                            }}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                  )}
                  {source === "Upload Data" && uploadedFileName !== "" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                      <Typography variant="body">
                        Successfully uploaded:  {uploadedFileName}
                      </Typography>
                      <Button variant="outlined" onClick={() => setUploadedFileName("")} sx={{ mt: 0 }}>
                        Re-upload
                      </Button>
                    </Box>
                  )}

                </RadioGroup>
              </FormControl>

            </Grid>
            <Grid item xs={4} sx={gridItemStyle}>
              <StatusBox message="Please select the source of your GWAS summary statistics." status={sourceStatus} />
            </Grid>

            {/* Trait Type */}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <Typography variant="h6">Trait Type:</Typography>
                <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2">
                    Information on trait type and sample size can be found via the dataset-specific "View Study" link on the <a href="/data" target="_blank">Queryable Data</a> page.
                  </Typography>
                </Box>
              </Grid>)}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <FormControl component="fieldset" sx={{ width: "100%" }}>
                  <RadioGroup
                    row
                    aria-label="traitType"
                    name="type"
                    value={type}
                  >
                    <FormControlLabel
                      value="Binary"
                      control={<Radio />}
                      label="Binary"
                      onChange={(e) => setType(e.target.value)}
                    />
                    <FormControlLabel
                      value="Continuous"
                      control={<Radio />}
                      label="Continuous"
                      onChange={(e) => setType(e.target.value)}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <StatusBox message={"Please choose the type of trait you are analyzing."} status={type === "" ? "required" : "ok"} />
              </Grid>
            )}

            {/* Sample Size (Query data only) */}
            {type !== "" && source === "Query Data" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <Typography variant="h6">
                  Sample Size:
                </Typography>
              </Grid>
            )}
            {type !== "" && source === "Query Data" && (
              <Grid item xs={4} sx={gridItemStyle}>
                {type === "Binary" && (
                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    <RadioGroup
                      row
                      value={nType}
                      onChange={(e) => setNType(e.target.value)}
                    >
                      <FormControlLabel
                        value="ncase"
                        control={<Radio />}
                        label="Ncase & Ncontrol"
                      />
                      <FormControlLabel
                        value="neff"
                        control={<Radio />}
                        label="Neff"
                      />

                    </RadioGroup>
                  </FormControl>)}
                {nType === "n" && (
                  <Grid item xs={12} sx={{ width: "100%" }}>
                    <TextField
                      label="N"
                      name="n"
                      value={colNames.n}
                      onChange={handleColNameChange}
                      margin="normal"
                      fullWidth
                      error={isNaN(Number(colNames.n)) && colNames.n !== ""}
                      helperText={isNaN(Number(colNames.n)) && colNames.n !== "" ? "Please enter a number." : ""}
                    />
                  </Grid>
                )}
                {nType === "ncase" && (
                  <Grid container spacing={1} sx={{ width: "100%" }}>
                    <Grid item xs={6}>
                      <TextField
                        label="N Case"
                        name="ncase"
                        value={colNames.ncase}
                        onChange={handleColNameChange}
                        margin="normal"
                        fullWidth
                        error={isNaN(Number(colNames.ncase)) && colNames.ncase !== ""}
                        helperText={isNaN(Number(colNames.ncase)) && colNames.ncase !== "" ? "Please enter a number." : ""}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="N Control"
                        name="ncontrol"
                        value={colNames.ncontrol}
                        onChange={handleColNameChange}
                        margin="normal"
                        fullWidth
                        error={isNaN(Number(colNames.ncontrol)) && colNames.ncontrol !== ""}
                        helperText={isNaN(Number(colNames.ncontrol)) && colNames.ncontrol !== "" ? "Please enter a number." : ""}
                      />
                    </Grid>
                  </Grid>
                )}
                {nType === "neff" && (
                  <Grid item xs={12} sx={{ width: "100%" }}>
                    <TextField
                      label="Neff"
                      name="neff"
                      value={colNames.neff}
                      onChange={handleColNameChange}
                      margin="normal"
                      fullWidth
                      error={isNaN(Number(colNames.neff)) && colNames.neff !== ""}
                      helperText={isNaN(Number(colNames.neff)) && colNames.neff !== "" ? "Please enter a number." : ""}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {type !== "" && source === "Query Data" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <StatusBox message="Please enter the sample size of your input data." status={sampleSizeStatus} />
              </Grid>
            )}


            {/* Trait Name */}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Trait Name:
                </Typography>
              </Grid>)}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <TextField
                  name="trait"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ width: "100%" }}
                />
              </Grid>)}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <StatusBox message="Please enter the name of your trait." status={name === "" ? "required" : "ok"} />
              </Grid>)}


            {/* Population */}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Population:
                </Typography>
                <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2">
                    We support analyses on one of the five <a href="https://useast.ensembl.org/Help/View?id=171">super populations</a> defined by the 1000 Genomes Project.
                  </Typography>
                </Box>
              </Grid>)}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                {/* <FormControl variant="outlined" fullWidth>
                  <Select
                    value={population}
                    onChange={(e) => setPopulation(e.target.value)}
                  >
                    <MenuItem value={"European (EUR)"}>
                      European (EUR)
                    </MenuItem>
                    <MenuItem value={"African or African American (AFR)"}>
                      African, Admixed African, African American (AFR)
                    </MenuItem>
                    <MenuItem value={"Admixed American (AMR)"}>
                      Admixed American,  Hispanic / Latino (AMR)
                    </MenuItem>
                    <MenuItem value={"East Asian (EAS)"}>
                      East Asian (EAS)
                    </MenuItem>
                    <MenuItem value={"South Asian (SAS)"}>
                      South Asian (SAS)
                    </MenuItem>
                  </Select>
                </FormControl> */}
                <FormControl variant="outlined" fullWidth>
                  {detail === "FinnGen" ? (
                    <Tooltip
                      title="Population is forced to European (EUR) because FinnGen was selected."
                      placement="top"
                    >
                      <span>
                        <Select
                          value={population}
                          onChange={(e) => setPopulation(e.target.value)}
                          disabled
                          fullWidth
                        >
                          <MenuItem value={"European (EUR)"}>
                            European (EUR)
                          </MenuItem>
                          <MenuItem value={"African or African American (AFR)"}>
                            African, Admixed African, African American (AFR)
                          </MenuItem>
                          <MenuItem value={"Admixed American (AMR)"}>
                            Admixed American,  Hispanic / Latino (AMR)
                          </MenuItem>
                          <MenuItem value={"East Asian (EAS)"}>
                            East Asian (EAS)
                          </MenuItem>
                          <MenuItem value={"South Asian (SAS)"}>
                            South Asian (SAS)
                          </MenuItem>
                        </Select>
                      </span>
                    </Tooltip>
                  ) : (
                    <Select
                      value={population}
                      onChange={(e) => setPopulation(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value={"European (EUR)"}>
                        European (EUR)
                      </MenuItem>
                      <MenuItem value={"African or African American (AFR)"}>
                        African, Admixed African, African American (AFR)
                      </MenuItem>
                      <MenuItem value={"Admixed American (AMR)"}>
                        Admixed American,  Hispanic / Latino (AMR)
                      </MenuItem>
                      <MenuItem value={"East Asian (EAS)"}>
                        East Asian (EAS)
                      </MenuItem>
                      <MenuItem value={"South Asian (SAS)"}>
                        South Asian (SAS)
                      </MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>)}
            {source !== "" && (
              <Grid item xs={4} sx={gridItemStyle}>
                <StatusBox message={"Please select the population of your input data."} status={population === "" ? "required" : "ok"} />
              </Grid>)}


            {/* Match Column Names */}
            {source !== "Query Data" && type !== "" && (
              <>
                <Grid item xs={4} sx={gridItemStyle}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Column Names:
                  </Typography>

                  <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                    <Typography variant="body2">
                      Please check <a href="https://pennprs.gitbook.io/pennprs/user-manual/single-ancestry-analysis#step-1.-build-input-gwas-summary-data-file" target="_blank">Tutorial</a> for more information.
                    </Typography>
                  </Box>
                </Grid>

                {type === "Binary" && (
                  <Grid item xs={4} sx={gridItemStyle}>
                    <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                      <Typography variant="body2">
                        Are the data columns already matched to the following (case insensitive)?
                      </Typography>
                      <Typography variant="body2" component="div">
                        <ul>
                          <li>CHR | #CHROM | Chromosome </li>
                          <li>SNP | ID | Snpid | Snpid_UKB | RS | Rsid | Rs_id</li>
                          <li>A1 | ALT | Effect_allele | allele1 | allele_1 | alt_allele | EA </li>
                          <li>A2 | REF | OMITTED | allele0 | allele2 | allele_2 | ref_allele | other_allele | NEA </li>
                          <li>BETA | OR | logOR | Effect </li>
                          <li>SE | LOG(OR)_SE | Stderr | Std_Error | Stderr_Beta | SE_Beta | Stderr_B </li>
                          <li>P | LOG10P | LP | Pvalue | P_value | pvalue | Pval | P_val | GC_Pvalue </li>
                          <li>MAF | AF | A1_FREQ | AAF | AF1 | A1FREQ | Effect_allele_frequency | Eaf | FRQ | FRQ_U | F_U</li>
                          <li>Neff | N_eff</li>
                          <li>Ncase | N_case</li>
                          <li>Ncontrol | N_control</li>
                        </ul>
                        We will delete the redundant columns that are not listed above.
                      </Typography>
                      <RadioGroup
                        row
                        value={columnsMatched}
                        onChange={(e) => setColumnsMatched(e.target.value)}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                )}
                {type === "Continuous" && (
                  <Grid item xs={4} sx={gridItemStyle}>
                    <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                      <Typography variant="body2">
                        Are the data columns already matched to the following (case insensitive)?
                      </Typography>
                      <Typography variant="body2" component="div">
                        <ul>
                          <li>CHR | #CHROM | Chromosome </li>
                          <li>SNP | ID | Snpid | Snpid_UKB | RS | Rsid | Rs_id</li>
                          <li>A1 | ALT | Effect_allele | allele1 | allele_1 | alt_allele | EA </li>
                          <li>A2 | REF | OMITTED | allele0 | allele2 | allele_2 | ref_allele | other_allele | NEA </li>
                          <li>BETA | OR | logOR | Effect </li>
                          <li>SE | LOG(OR)_SE | Stderr | Std_Error | Stderr_Beta | SE_Beta | Stderr_B </li>
                          <li>P | LOG10P | LP | Pvalue | P_value | pvalue | Pval | P_val | GC_Pvalue </li>
                          <li>MAF | AF | A1_FREQ | AAF | AF1 | A1FREQ | Effect_allele_frequency | Eaf | FRQ | FRQ_U | F_U</li>
                          <li>N | n | OBS_CT </li>
                        </ul>
                        We will delete the redundant columns that are not listed above.
                      </Typography>
                      <RadioGroup
                        row
                        value={columnsMatched}
                        onChange={(e) => setColumnsMatched(e.target.value)}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                )}


                <Grid item xs={4} sx={gridItemStyle}>
                  <StatusBox message={"Please confirm if your columns are matched to the required format."} status={columnsMatched === null ? "required" : colNamesStatus} />
                </Grid>
              </>
            )}

            {source !== "Query Data" && columnsMatched === "no" && (
              <>
                <Grid item xs={4} sx={gridItemStyle}>
                  <Box sx={{ bgcolor: "grey.300", p: 1, borderRadius: 1 }}>
                    <Typography variant="body2">
                      <b>Please enter column names that match the required format.</b>
                    </Typography>
                    <Typography variant="body2">
                      Please fill in the boxes for names that do not match with ours. If they match, no action is needed.
                    </Typography>
                    <Typography variant="body2">
                      <br></br> <b>NOTE:</b> Required format for the SNP column: RSID (e.g., rs3094315).
                    </Typography>

                    {type === "Binary" && (
                      <Typography variant="body2" component="div">
                        <br></br><b>NOTE:</b> If you are using Odds Ratio instead of BETA, please prepare SE for logOR.
                      </Typography>
                    )}

                    {/* {type === "Binary" && (
                      <Typography variant="body2" component="div">
                        <br></br> <b>Neff</b>: Effective sample size = 4/(1/Ncase + 1/N_control)
                        <br></br> <b>Ncase & Ncontrol</b>: number of cases & controls
                      </Typography>
                    )} */}
                    {type === "Binary" && (
                      <Typography variant="body2" component="div">
                        <br></br><br></br>Your data should include either two separate columns for the number of cases and controls (i.e., “Ncase” and “Ncontrol”) or one column for the effective sample size (i.e., “Neff”). Please ensure your data includes these columns and input their column names.
                      </Typography>
                    )}
                  </Box></Grid>
                <Grid item xs={4} sx={gridItemStyle}>
                  <FormControl component="fieldset">
                    <form noValidate autoComplete="off">
                      <Grid container spacing={2}>

                        <Grid item xs={6}>
                          <TextField
                            label="CHROMOSOME"
                            name="chr"
                            value={colNames.chr}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="SNP/RSID"
                            name="snp"
                            value={colNames.snp}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="A1"
                            name="a1"
                            value={colNames.a1}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="A2"
                            name="a2"
                            value={colNames.a2}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label={type === "Binary" ? "BETA/Odds Ratio" : "BETA"}
                            name="beta"
                            value={colNames.beta}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="SE"
                            name="se"
                            value={colNames.se}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="P"
                            name="p"
                            value={colNames.p}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="MAF/EAF"
                            name="maf"
                            value={colNames.maf}
                            onChange={handleColNameChange}
                            margin="normal"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {type === "Binary" && (
                            <FormControl component="fieldset">
                              <RadioGroup
                                row
                                value={nType}
                                onChange={(e) => setNType(e.target.value)}
                              >
                                <>
                                  <FormControlLabel
                                    value="neff"
                                    control={<Radio />}
                                    label="Neff"
                                  />
                                  <FormControlLabel
                                    value="ncase"
                                    control={<Radio />}
                                    label="Ncase & Ncontrol"
                                  />
                                </>
                              </RadioGroup>
                            </FormControl>)}

                          {nType === "n" && (
                            <Grid item xs={12}>
                              <TextField
                                label="N"
                                name="n"
                                value={colNames.n}
                                onChange={handleColNameChange}
                                margin="normal"
                                fullWidth
                              />
                            </Grid>
                          )}
                          {nType === "ncase" && (
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <TextField
                                  label="N Case"
                                  name="ncase"
                                  value={colNames.ncase}
                                  onChange={handleColNameChange}
                                  margin="normal"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  label="N Control"
                                  name="ncontrol"
                                  value={colNames.ncontrol}
                                  onChange={handleColNameChange}
                                  margin="normal"
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          )}
                          {nType === "neff" && (
                            <Grid item xs={12}>
                              <TextField
                                label="Neff"
                                name="neff"
                                value={colNames.neff}
                                onChange={handleColNameChange}
                                margin="normal"
                                fullWidth
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </form>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sx={gridItemStyle}>
                  <StatusBox message={"Entering the column names of your input data is optional but recommended. If not provided, we will automatically detect the column names of your input data."} status={colNamesStatus} />
                </Grid>
              </>
            )}

            {/* Save & Continue Button */}
            <Grid item xs={1} />
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setStep(0)}
                sx={{
                  color: "white",
                  backgroundColor: "grey",
                  mb: 2,
                  mt: -1,
                  textTransform: 'none',
                  fontSize: 18,
                  fontWeight: 'bold',
                  "&:hover": { backgroundColor: "#0d47a1" },
                  mr: 2 // margin-right to add space between buttons
                }}
              >
                Back to Introduction
              </Button>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  color: "white",
                  backgroundColor: "grey",
                  mb: 2,
                  mt: -1,
                  fontSize: 18,
                  fontWeight: 'bold',
                  "&:hover": { backgroundColor: "#0d47a1" },
                }}
              >
                Save & Continue
              </Button>
            </Grid>


          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
